import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';

const COLORS = {
  primary: '#FF6B35', bg: '#F8FAFC', white: '#FFFFFF',
  border: '#E2E8F0', text1: '#1E293B', text2: '#64748B', text3: '#94A3B8',
  green: '#10B981', red: '#EF4444', yellow: '#F59E0B', purple: '#8B5CF6',
};

const EMPTY_FORM = {
  company_name: '', email_domain: '', bonus_multiplier: 1.5,
  category: 'bank', description: '', is_active: true, logo_url: '',
};

const CATEGORIES = [
  { value: 'bank',        label: '🏦 Bank' },
  { value: 'energy',      label: '⚡ Enerji / Neft' },
  { value: 'university',  label: '🎓 Universitet' },
  { value: 'telecom',     label: '📡 Telekommunikasiya' },
  { value: 'insurance',   label: '🛡️ Sığorta' },
  { value: 'government',  label: '🏛️ Dövlət' },
  { value: 'tech',        label: '💻 Texnologiya' },
  { value: 'other',       label: '🏢 Digər' },
];

const MULTIPLIER_PRESETS = [1.25, 1.5, 1.75, 2.0, 2.5, 3.0];

export default function CorporatePage() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, users: 0 });

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('corporate_partners')
      .select('*')
      .order('created_at', { ascending: false });
    setPartners(data || []);

    // Stats
    const active = (data || []).filter(p => p.is_active).length;
    setStats({ total: (data || []).length, active, users: 0 });
    setLoading(false);
  };

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      company_name: p.company_name,
      email_domain: p.email_domain,
      bonus_multiplier: p.bonus_multiplier,
      category: p.category || 'other',
      description: p.description || '',
      is_active: p.is_active,
      logo_url: p.logo_url || '',
    });
    setShowForm(true);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMsg('⏳ Logo yüklənir...');
    const ext = file.name.split('.').pop();
    const path = `corp-logos/${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage.from('business-photos').upload(path, file, { upsert: true });
    if (upErr) return setMsg('❌ ' + upErr.message);
    const { data } = supabase.storage.from('business-photos').getPublicUrl(path);
    setForm(f => ({ ...f, logo_url: data.publicUrl }));
    setMsg('✅ Logo yükləndi');
  };

  const save = async () => {
    if (!form.company_name.trim() || !form.email_domain.trim()) {
      return setMsg('❌ Şirkət adı və email domen mütləqdir');
    }
    if (!/^[a-z0-9.-]+\.[a-z]{2,}$/.test(form.email_domain.trim().toLowerCase())) {
      return setMsg('❌ Domen formatı düzgün deyil (məs: pashabank.az)');
    }
    setSaving(true);
    setMsg('');
    const payload = { ...form, email_domain: form.email_domain.trim().toLowerCase() };
    let error;
    if (editing) {
      ({ error } = await supabase.from('corporate_partners').update(payload).eq('id', editing));
    } else {
      ({ error } = await supabase.from('corporate_partners').insert(payload));
    }
    setSaving(false);
    if (error) return setMsg('❌ ' + error.message);
    setMsg('✅ Saxlandı');
    setShowForm(false);
    load();
  };

  const toggleActive = async (id, current) => {
    await supabase.from('corporate_partners').update({ is_active: !current }).eq('id', id);
    setPartners(prev => prev.map(p => p.id === id ? { ...p, is_active: !current } : p));
  };

  const remove = async (id) => {
    if (!window.confirm('Silmək istədiyinizə əminsiniz?')) return;
    await supabase.from('corporate_partners').delete().eq('id', id);
    setPartners(prev => prev.filter(p => p.id !== id));
  };

  const filtered = partners.filter(p =>
    p.company_name.toLowerCase().includes(search.toLowerCase()) ||
    p.email_domain.toLowerCase().includes(search.toLowerCase())
  );

  const getCategoryLabel = (cat) => CATEGORIES.find(c => c.value === cat)?.label || '🏢 Digər';
  const getMultiplierColor = (m) => m >= 2 ? COLORS.green : m >= 1.5 ? COLORS.yellow : COLORS.text2;

  return (
    <div style={{ padding: 28, background: COLORS.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: COLORS.text1, margin: 0 }}>🏢 Korporativ Tərəfdaşlar</h1>
          <p style={{ fontSize: 14, color: COLORS.text2, marginTop: 4 }}>
            Korporativ email domenləri ilə qeydiyyat edən istifadəçilərə əlavə xal bonusu
          </p>
        </div>
        <button onClick={openAdd} style={{
          background: COLORS.primary, color: '#fff', border: 'none',
          padding: '11px 22px', borderRadius: 10, fontWeight: 700,
          fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          + Yeni Tərəfdaş
        </button>
      </div>

      {msg && (
        <div style={{
          marginBottom: 16, padding: '12px 16px', borderRadius: 10,
          background: msg.startsWith('✅') ? '#F0FDF4' : '#FEF2F2',
          border: `1px solid ${msg.startsWith('✅') ? '#BBF7D0' : '#FECACA'}`,
          color: msg.startsWith('✅') ? COLORS.green : COLORS.red, fontWeight: 600, fontSize: 14,
        }}>{msg}</div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
        {[
          { label: 'Ümumi Tərəfdaş', value: stats.total, color: COLORS.primary, icon: '🏢' },
          { label: 'Aktiv', value: stats.active, color: COLORS.green, icon: '✅' },
          { label: 'Passiv', value: stats.total - stats.active, color: COLORS.text3, icon: '⏸️' },
        ].map((s, i) => (
          <div key={i} style={{ background: COLORS.white, borderRadius: 12, padding: '18px 20px', border: `1px solid ${COLORS.border}` }}>
            <div style={{ fontSize: 22 }}>{s.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: s.color, lineHeight: 1.2, marginTop: 6 }}>{s.value}</div>
            <div style={{ fontSize: 13, color: COLORS.text2, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Şirkət adı və ya domen axtar..."
          style={{
            width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 14,
            border: `1px solid ${COLORS.border}`, outline: 'none', boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Table */}
      <div style={{ background: COLORS.white, borderRadius: 14, border: `1px solid ${COLORS.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F8FAFC', borderBottom: `1px solid ${COLORS.border}` }}>
              {['Şirkət', 'Email Domen', 'Kateqoriya', 'Xal Çarpanı', 'Status', 'Əməliyyat'].map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: COLORS.text2, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: COLORS.text3 }}>Yüklənir...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 40, textAlign: 'center', color: COLORS.text3 }}>
                {search ? 'Axtarışa uyğun nəticə yoxdur' : 'Hələ heç bir korporativ tərəfdaş yoxdur'}
              </td></tr>
            ) : filtered.map(p => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.text1 }}>{p.company_name}</div>
                  {p.description && <div style={{ fontSize: 12, color: COLORS.text3, marginTop: 2 }}>{p.description}</div>}
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{
                    background: '#EFF6FF', color: '#3B82F6', fontSize: 12,
                    fontWeight: 600, padding: '3px 10px', borderRadius: 6, fontFamily: 'monospace',
                  }}>@{p.email_domain}</span>
                </td>
                <td style={{ padding: '14px 16px', fontSize: 13, color: COLORS.text2 }}>
                  {getCategoryLabel(p.category)}
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{
                    fontWeight: 800, fontSize: 16, color: getMultiplierColor(p.bonus_multiplier),
                  }}>{p.bonus_multiplier}x</span>
                  <div style={{ fontSize: 11, color: COLORS.text3, marginTop: 1 }}>xal çarpanı</div>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <button onClick={() => toggleActive(p.id, p.is_active)} style={{
                    padding: '4px 12px', borderRadius: 20, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700,
                    background: p.is_active ? '#D1FAE5' : '#F1F5F9',
                    color: p.is_active ? COLORS.green : COLORS.text3,
                  }}>{p.is_active ? '✅ Aktiv' : '⏸️ Passiv'}</button>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => openEdit(p)} style={{
                      padding: '6px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
                      background: '#fff', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: COLORS.text2,
                    }}>Düzəlt</button>
                    <button onClick={() => remove(p.id)} style={{
                      padding: '6px 14px', borderRadius: 8, border: 'none',
                      background: '#FEE2E2', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: COLORS.red,
                    }}>Sil</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20,
        }}>
          <div style={{
            background: COLORS.white, borderRadius: 16, padding: 32, width: '100%',
            maxWidth: 520, maxHeight: '90vh', overflowY: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: COLORS.text1, margin: 0 }}>
                {editing ? 'Tərəfdaşı Düzəlt' : 'Yeni Korporativ Tərəfdaş'}
              </h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: COLORS.text3 }}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {/* Şirkət adı */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Şirkət Adı *</label>
                <input
                  value={form.company_name}
                  onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))}
                  placeholder="məs: Pasha Bank"
                  style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {/* Email domain */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Email Domen *</label>
                <div style={{ display: 'flex', alignItems: 'center', border: `1px solid ${COLORS.border}`, borderRadius: 9, overflow: 'hidden' }}>
                  <span style={{ padding: '10px 12px', background: '#F8FAFC', color: COLORS.text3, fontSize: 14, borderRight: `1px solid ${COLORS.border}` }}>@</span>
                  <input
                    value={form.email_domain}
                    onChange={e => setForm(f => ({ ...f, email_domain: e.target.value.toLowerCase() }))}
                    placeholder="pashabank.az"
                    style={{ flex: 1, padding: '10px 13px', border: 'none', fontSize: 14, outline: 'none', fontFamily: 'monospace' }}
                  />
                </div>
                <p style={{ fontSize: 11, color: COLORS.text3, marginTop: 4 }}>Bu domenli emaillər korporativ hesab kimi tanınacaq</p>
              </div>

              {/* Kateqoriya */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Kateqoriya</label>
                <select
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', background: '#fff', boxSizing: 'border-box' }}
                >
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              {/* Xal çarpanı */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>
                  Xal Çarpanı — <span style={{ color: getMultiplierColor(form.bonus_multiplier), fontSize: 16 }}>{form.bonus_multiplier}x</span>
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                  {MULTIPLIER_PRESETS.map(m => (
                    <button key={m} onClick={() => setForm(f => ({ ...f, bonus_multiplier: m }))} style={{
                      padding: '6px 14px', borderRadius: 8, border: `2px solid ${form.bonus_multiplier === m ? COLORS.primary : COLORS.border}`,
                      background: form.bonus_multiplier === m ? '#FFF4EF' : '#fff',
                      color: form.bonus_multiplier === m ? COLORS.primary : COLORS.text2,
                      fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}>{m}x</button>
                  ))}
                </div>
                <input
                  type="number" min="1" max="10" step="0.25"
                  value={form.bonus_multiplier}
                  onChange={e => setForm(f => ({ ...f, bonus_multiplier: parseFloat(e.target.value) || 1 }))}
                  style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
                <p style={{ fontSize: 11, color: COLORS.text3, marginTop: 4 }}>
                  {form.bonus_multiplier}x = hər 10 xal əvəzinə {Math.round(10 * form.bonus_multiplier)} xal
                </p>
              </div>

              {/* Açıqlama */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Açıqlama (istəyə bağlı)</label>
                <input
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="məs: Əsas tərəfdaş, VIP status"
                  style={{ width: '100%', padding: '10px 13px', borderRadius: 9, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>

              {/* Logo */}
              <div>
                <label style={{ fontSize: 13, fontWeight: 700, color: COLORS.text2, display: 'block', marginBottom: 6 }}>Şirkət Logosu</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {form.logo_url && (
                    <img src={form.logo_url} alt="logo" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'contain', border: `1px solid ${COLORS.border}`, background: '#F8FAFC', padding: 4 }} />
                  )}
                  <label style={{
                    padding: '8px 16px', borderRadius: 9, border: `1px solid ${COLORS.border}`,
                    background: '#F8FAFC', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: COLORS.text2,
                  }}>
                    {form.logo_url ? 'Dəyişdir' : '+ Logo Yüklə'}
                    <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ display: 'none' }} />
                  </label>
                  {form.logo_url && (
                    <button onClick={() => setForm(f => ({ ...f, logo_url: '' }))} style={{ background: 'none', border: 'none', color: COLORS.red, cursor: 'pointer', fontSize: 13 }}>Sil</button>
                  )}
                </div>
              </div>

              {/* Aktiv */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <input
                  type="checkbox" id="is_active"
                  checked={form.is_active}
                  onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                <label htmlFor="is_active" style={{ fontSize: 14, color: COLORS.text1, cursor: 'pointer', fontWeight: 600 }}>
                  Aktiv — qeydiyyat zamanı tanınsın
                </label>
              </div>
            </div>

            {msg && (
              <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                background: msg.startsWith('✅') ? '#F0FDF4' : '#FEF2F2',
                color: msg.startsWith('✅') ? COLORS.green : COLORS.red,
              }}>{msg}</div>
            )}

            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
              <button onClick={() => setShowForm(false)} style={{
                flex: 1, padding: '12px', borderRadius: 10, border: `1px solid ${COLORS.border}`,
                background: '#fff', color: COLORS.text2, fontWeight: 600, fontSize: 14, cursor: 'pointer',
              }}>Ləğv et</button>
              <button onClick={save} disabled={saving} style={{
                flex: 2, padding: '12px', borderRadius: 10, border: 'none',
                background: COLORS.primary, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer',
                opacity: saving ? 0.7 : 1,
              }}>{saving ? 'Saxlanır...' : editing ? 'Yenilə' : 'Əlavə Et'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
